// Activity Service - Manages farm activities and tasks (Multi-tenant)
import { tenantService } from './tenantService';

export interface Activity {
  id: string;
  tenantId: string;
  title: string;
  description: string;
  type: 'feeding' | 'cleaning' | 'vaccination' | 'harvest' | 'maintenance' | 'other' | 'chick';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string;
  batchId?: string;
  scheduledDate: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  completedDate?: string;
  estimatedDuration: number; // in minutes
  actualDuration?: number; // in minutes
  location: string;
  notes?: string;
  attachments?: string[];
  progress?: number;
  createdAt: string;
  updatedAt: string;
}

class ActivityService {
  private activities: Activity[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // Load data from localStorage
  private loadFromStorage(): void {
    try {
      // Use default tenant ID if tenantService is not ready
      const currentTenant = tenantService.getCurrentTenant();
      const tenantId = currentTenant?.id || 'default_tenant';
      const storageKey = `activities_${tenantId}`;
      const activitiesData = localStorage.getItem(storageKey);
      if (activitiesData) {
        this.activities = JSON.parse(activitiesData);
      }
    } catch (error) {
      console.error('Error loading activities from storage:', error);
    }
  }

  // Save data to localStorage
  private saveToStorage(): void {
    try {
      // Use default tenant ID if tenantService is not ready
      const currentTenant = tenantService.getCurrentTenant();
      const tenantId = currentTenant?.id || 'default_tenant';
      const storageKey = `activities_${tenantId}`;
      localStorage.setItem(storageKey, JSON.stringify(this.activities));
    } catch (error) {
      console.error('Error saving activities to storage:', error);
    }
  }

  // Get all activities for current tenant
  getAllActivities(): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.activities.filter(activity => activity.tenantId === currentTenant.id);
  }

  // Get activity by ID
  getActivityById(id: string): Activity | undefined {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return undefined;
    
    return this.activities.find(activity => activity.id === id && activity.tenantId === currentTenant.id);
  }

  // Get activities by status
  getActivitiesByStatus(status: Activity['status']): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.activities.filter(activity => 
      activity.status === status && activity.tenantId === currentTenant.id
    );
  }

  // Get activities by type
  getActivitiesByType(type: Activity['type']): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.activities.filter(activity => 
      activity.type === type && activity.tenantId === currentTenant.id
    );
  }

  // Get activities for a specific batch
  getActivitiesByBatch(batchId: string): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.activities.filter(activity => 
      activity.batchId === batchId && activity.tenantId === currentTenant.id
    );
  }

  // Get activities for today
  getTodayActivities(): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    const today = new Date().toISOString().split('T')[0];
    return this.activities.filter(activity => 
      activity.scheduledDate.startsWith(today) && activity.tenantId === currentTenant.id
    );
  }

  // Get upcoming activities (next 7 days)
  getUpcomingActivities(): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return this.activities.filter(activity => {
      const activityDate = new Date(activity.scheduledDate);
      return activityDate >= today && 
             activityDate <= nextWeek && 
             activity.status !== 'completed' &&
             activity.tenantId === currentTenant.id;
    });
  }

  // Create new activity
  createActivity(activityData: Omit<Activity, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): Activity {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) {
      throw new Error('No active tenant found');
    }

    const now = new Date().toISOString();
    const newActivity: Activity = {
      ...activityData,
      id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      tenantId: currentTenant.id,
      createdAt: now,
      updatedAt: now,
    };

    this.activities.push(newActivity);
    this.saveToStorage();
    return newActivity;
  }

  // Update activity
  updateActivity(id: string, updates: Partial<Activity>): Activity | null {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return null;
    
    const index = this.activities.findIndex(activity => 
      activity.id === id && activity.tenantId === currentTenant.id
    );
    if (index === -1) return null;

    this.activities[index] = { 
      ...this.activities[index], 
      ...updates, 
      updatedAt: new Date().toISOString() 
    };
    this.saveToStorage();
    return this.activities[index];
  }

  // Delete activity
  deleteActivity(id: string): boolean {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return false;
    
    const index = this.activities.findIndex(activity => 
      activity.id === id && activity.tenantId === currentTenant.id
    );
    if (index === -1) return false;

    this.activities.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  // Mark activity as completed
  completeActivity(id: string, actualDuration?: number, notes?: string): Activity | null {
    const now = new Date().toISOString();
    return this.updateActivity(id, {
      status: 'completed',
      completedDate: now,
      actualDuration,
      notes: notes ? `${this.activities.find(a => a.id === id)?.notes || ''}\nCompleted: ${notes}`.trim() : undefined,
    });
  }

  // Get activity statistics
  getActivityStatistics() {
    const totalActivities = this.activities.length;
    const pendingActivities = this.activities.filter(a => a.status === 'pending').length;
    const inProgressActivities = this.activities.filter(a => a.status === 'in_progress').length;
    const completedActivities = this.activities.filter(a => a.status === 'completed').length;
    const overdueActivities = this.activities.filter(a => {
      if (a.status === 'completed') return false;
      const scheduledDate = new Date(a.scheduledDate);
      const today = new Date();
      return scheduledDate < today;
    }).length;

    const averageCompletionTime = completedActivities > 0 
      ? this.activities
          .filter(a => a.status === 'completed' && a.actualDuration)
          .reduce((sum, a) => sum + (a.actualDuration || 0), 0) / 
          this.activities.filter(a => a.status === 'completed' && a.actualDuration).length
      : 0;

    return {
      totalActivities,
      pendingActivities,
      inProgressActivities,
      completedActivities,
      overdueActivities,
      averageCompletionTime: Math.round(averageCompletionTime),
    };
  }

  // Get activities by priority
  getActivitiesByPriority(priority: Activity['priority']): Activity[] {
    return this.activities.filter(activity => activity.priority === priority);
  }

  // Search activities
  searchActivities(query: string): Activity[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    const lowercaseQuery = query.toLowerCase();
    return this.activities.filter(activity => 
      activity.tenantId === currentTenant.id &&
      (activity.title.toLowerCase().includes(lowercaseQuery) ||
       activity.description.toLowerCase().includes(lowercaseQuery) ||
       activity.type.toLowerCase().includes(lowercaseQuery) ||
       activity.location.toLowerCase().includes(lowercaseQuery))
    );
  }

  // Load activities (alias for getAllActivities for backward compatibility)
  loadActivities(): Activity[] {
    return this.getAllActivities();
  }

  // Add activity (alias for createActivity for backward compatibility)
  addActivity(activityData: Omit<Activity, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): Activity {
    return this.createActivity(activityData);
  }

  // Clear all activities (for demo/reset purposes)
  clearAllActivities(): void {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return;

    this.activities = this.activities.filter(activity => activity.tenantId !== currentTenant.id);
    this.saveToStorage();
  }

  // Force reset (alias for clearAllActivities for backward compatibility)
  forceReset(): void {
    this.clearAllActivities();
  }
}

// Export singleton instance
export const activityService = new ActivityService();
